function legacyCopyText(text: string): boolean {
  if (typeof document === "undefined" || typeof document.execCommand !== "function") {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.setAttribute("aria-hidden", "true");
  Object.assign(textarea.style, {
    position: "fixed",
    insetInlineStart: "0",
    top: "0",
    width: "2px",
    height: "2px",
    padding: "0",
    border: "0",
    fontSize: "16px",
    opacity: "0.01",
    pointerEvents: "none"
  });
  document.body.appendChild(textarea);

  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

/**
 * Clipboard support in wallet WebViews is inconsistent: some expose writeText()
 * but reject it asynchronously. Start the modern attempt and complete the
 * textarea fallback before yielding, while the original tap still counts as a
 * user activation. Both paths write the same text, so running them together is
 * safe and avoids losing the activation window on iOS/Android wallet browsers.
 */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;

  let clipboardAttempt: Promise<boolean> | null = null;
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      clipboardAttempt = Promise.resolve(navigator.clipboard.writeText(text)).then(
        () => true,
        () => false
      );
    } catch {
      // Some partially implemented wallet WebViews expose writeText() but throw
      // synchronously. Keep going so the legacy path can still handle the tap.
      clipboardAttempt = Promise.resolve(false);
    }
  }

  // This must run before the first await. Several wallet WebViews require
  // execCommand("copy") to remain in the direct click/tap call stack.
  const legacyCopied = legacyCopyText(text);
  if (legacyCopied) {
    if (clipboardAttempt) void clipboardAttempt;
    return true;
  }

  return clipboardAttempt ? await clipboardAttempt : false;
}
